import { useRef, useState } from 'react';
import { useT } from '../../i18n';
import { QUICK_EMOJI } from './emoji';

interface Props {
  onSubmit: (bodyHtml: string) => void;
  submitting?: boolean;
}

/** Keep only <b>/<i> (and their <strong>/<em> aliases) + text; drop everything
 *  else. The server re-sanitizes authoritatively — this is just for a clean
 *  payload and to avoid sending hostile markup. */
function clientSanitize(root: HTMLElement): string {
  const allowed = new Set(['B', 'STRONG', 'I', 'EM']);
  const walk = (node: Node): string => {
    let out = '';
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += (child.textContent ?? '').replace(/[<>&]/g, (c) =>
          c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&amp;',
        );
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (el.tagName === 'BR') {
          out += '\n';
        } else if (allowed.has(el.tagName)) {
          const tag = el.tagName === 'STRONG' ? 'b' : el.tagName === 'EM' ? 'i' : el.tagName.toLowerCase();
          const inner = walk(el);
          if (inner.trim() !== '') out += `<${tag}>${inner}</${tag}>`;
        } else {
          // Block elements imply a line break; inline unknowns just unwrap.
          const block = ['P', 'DIV', 'LI', 'UL', 'OL'].includes(el.tagName);
          const inner = walk(el);
          out += block && inner !== '' ? inner + '\n' : inner;
        }
      }
    });
    return out;
  };
  return walk(root).replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Minimal rich-text composer: plain text + Bold/Italic + emoji. Uses a
 * contentEditable surface with document.execCommand for bold/italic — the
 * pragmatic cross-browser choice for this constrained format.
 */
export function CommentComposer({ onSubmit, submitting = false }: Props) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [empty, setEmpty] = useState(true);

  const refresh = () => {
    const el = ref.current;
    setEmpty(!el || el.textContent?.trim() === '');
  };

  const format = (cmd: 'bold' | 'italic') => {
    ref.current?.focus();
    // eslint-disable-next-line deprecation/deprecation
    document.execCommand(cmd);
    refresh();
  };

  const insertEmoji = (emoji: string) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    // eslint-disable-next-line deprecation/deprecation
    if (!document.execCommand('insertText', false, emoji)) {
      el.append(emoji);
    }
    refresh();
  };

  const submit = () => {
    const el = ref.current;
    if (!el) return;
    const html = clientSanitize(el);
    if (html.trim() === '') return;
    onSubmit(html);
    el.innerHTML = '';
    setEmpty(true);
  };

  return (
    <div className="rounded-2xl border border-line bg-surface p-2.5">
      <div className="mb-2 flex items-center gap-1">
        <button
          type="button"
          onClick={() => format('bold')}
          aria-label={t('engagement.bold', 'Bold')}
          className="h-7 w-7 rounded-lg text-[14px] font-bold text-ink-2 hover:bg-grey-soft"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => format('italic')}
          aria-label={t('engagement.italic', 'Italic')}
          className="h-7 w-7 rounded-lg text-[14px] italic text-ink-2 hover:bg-grey-soft"
        >
          I
        </button>
        <span className="mx-1 h-4 w-px bg-line" />
        {QUICK_EMOJI.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => insertEmoji(e)}
            className="h-7 w-7 rounded-lg text-[16px] hover:bg-grey-soft"
          >
            {e}
          </button>
        ))}
      </div>

      <div className="relative">
        <div
          ref={ref}
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label={t('engagement.commentPlaceholder', 'Write a comment…')}
          onInput={refresh}
          className="max-h-40 min-h-[40px] overflow-y-auto whitespace-pre-wrap break-words rounded-xl bg-grey-soft/60 px-3 py-2 text-[14px] leading-relaxed text-ink outline-none focus:bg-grey-soft"
        />
        {empty && (
          <span className="pointer-events-none absolute left-3 top-2 text-[14px] text-ink-3">
            {t('engagement.commentPlaceholder', 'Write a comment…')}
          </span>
        )}
      </div>

      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={empty || submitting}
          className="rounded-full px-4 py-2 text-[13px] font-semibold text-accent-ink disabled:opacity-40"
          style={{ background: 'color-mix(in oklch, var(--color-accent) 14%, var(--color-bg))' }}
        >
          {submitting ? t('common.adding', 'Adding…') : t('engagement.post', 'Post')}
        </button>
      </div>
    </div>
  );
}
